import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert
} from 'reactstrap';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { customDropdownWithBorder } from '../../util/reactCustomStyles';
import { generateQuestionsWithAI } from '../../redux/QuestionBank/AIGenerationAction';
import { fetchSubjects, fetchUnits, fetchTopics } from '../../redux/QuestionBank/SubjectsUnitsAction';
import { fetchPassages } from '../../redux/QuestionBank/PassageAction';
import { isImageGenerationAvailable } from '../../util/imageService';

const EXAM_TYPE_OPTIONS = [
  { value: 'mcat', label: 'MCAT (Medical College Admission Test)' },
  { value: 'dat', label: 'DAT (Dental Admission Test)' },
  { value: 'pmr', label: 'PM&R (Physical Medicine & Rehabilitation)' }
];

const EXAM_TYPE_LABELS = {
  mcat: 'MCAT (Medical College Admission Test)',
  dat: 'DAT (Dental Admission Test)',
  pmr: 'PM&R (Physical Medicine & Rehabilitation)'
};

const AIQuestionGenerator = ({ userEmail }) => {
  const dispatch = useDispatch();

  // Redux state
  const { subjects } = useSelector(state => state.questionBankSubjects);
  const { units } = useSelector(state => state.questionBankUnits);
  const { topics } = useSelector(state => state.questionBankTopics);
  const { passages } = useSelector(state => state.questionBankPassages);
  const { loading, generationInProgress } = useSelector(state => state.aiGeneration);

  // Form state
  const [formData, setFormData] = useState({
    subjectId: '',
    unitId: '',
    topicId: '',
    examType: 'mcat',
    difficulty: 'medium',
    aiModel: 'gemini',
    count: 10,
    numberOfChoices: 4,
    additionalContext: '',
    isPassageBased: false,
    passageMode: 'new', // 'new' or 'existing'
    existingPassageId: '',
    generateFigure: false
  });

  // Check if image generation API is configured
  const imageGenAvailable = isImageGenerationAvailable();

  // Filtered units based on selected subject
  const filteredUnits = formData.subjectId
    ? units.filter(unit => unit.subjectId === formData.subjectId)
    : [];

  // Filtered topics based on selected unit
  const filteredTopics = formData.unitId
    ? topics.filter(topic => topic.unitId === formData.unitId)
    : [];

  // Filtered passages based on selected subject and unit
  const filteredPassages = formData.subjectId && formData.unitId
    ? passages.filter(passage =>
        passage.subjectId === formData.subjectId &&
        passage.unitId === formData.unitId
      )
    : [];

  useEffect(() => {
    dispatch(fetchSubjects());
    dispatch(fetchUnits());
    dispatch(fetchTopics());
    dispatch(fetchPassages());
  }, [dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.subjectId) {
      Swal.fire('Error', 'Please select a subject', 'error');
      return;
    }

    if (!formData.unitId) {
      Swal.fire('Error', 'Please select a unit/discipline', 'error');
      return;
    }

    if (formData.count < 1 || formData.count > 20) {
      Swal.fire('Error', 'Number of questions must be between 1 and 20', 'error');
      return;
    }

    // Validate existing passage selection
    if (formData.isPassageBased && formData.passageMode === 'existing' && !formData.existingPassageId) {
      Swal.fire('Error', 'Please select an existing passage', 'error');
      return;
    }

    // Show loading for question generation
    Swal.fire({
      title: 'Generating Questions',
      text: `Generating ${formData.count} questions with AI...`,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    // Prepare params for AI generation - include names for the AI prompt
    const params = {
      ...formData,
      isPassageBased: formData.isPassageBased,
      subjectName: subjects.find(s => s.subjectId === formData.subjectId)?.name || '',
      unitName: filteredUnits.find(u => u.unitId === formData.unitId)?.name || '',
      topicName: formData.topicId
        ? filteredTopics.find(t => t.topicId === formData.topicId)?.name || ''
        : '',
      // Flag to generate figure after passage is created
      generateFigure: formData.generateFigure && imageGenAvailable
    };

    // If using existing passage, fetch it and pass along
    let existingPassage = null;
    if (formData.isPassageBased && formData.passageMode === 'existing') {
      existingPassage = passages.find(p => p.passageId === formData.existingPassageId);
    }

    // Generate questions
    const result = await dispatch(generateQuestionsWithAI(params, userEmail, existingPassage));

    Swal.close();

    if (result.success) {
      const figureMsg = result.passage?.figureUrl ? ' A figure was also generated for the passage.' : '';
      Swal.fire(
        'Success!',
        `Generated ${result.questions.length} questions successfully.${figureMsg} Review them below.`,
        'success'
      );
    } else {
      Swal.fire('Error', result.error, 'error');
    }
  };

  const getPassageCheckboxLabel = () => {
    switch (formData.examType) {
      case 'dat':
        return 'Generate passage-based questions (DAT science passages)';
      case 'pmr':
        return 'Generate passage-based questions (PM&R clinical vignettes)';
      default:
        return 'Generate passage-based questions (MCAT research passages)';
    }
  };

  const getPassageDescription = () => {
    if (!formData.isPassageBased) {
      return 'Generate standalone questions without a shared passage';
    }
    switch (formData.examType) {
      case 'dat':
        return 'AI will generate questions based on a science reading passage';
      case 'pmr':
        return 'AI will generate questions based on a clinical vignette';
      default:
        return 'AI will generate questions based on a research passage';
    }
  };

  return (
    <Card className="shadow">
      <CardHeader className="bg-transparent">
        <h3 className="mb-0">AI Question Generator</h3>
        <p className="text-sm text-muted mb-0 mt-2">
          Generate multiple-choice questions automatically using AI based on your topic and context.
        </p>
      </CardHeader>
      <CardBody>
        <Alert color="warning">
          <strong>Before you start:</strong> Make sure you have set up your subjects and units in the
          {' '}<strong>"Manage Subjects & Units"</strong> tab. The AI will use your selected subject, unit, and optionally topic
          to generate relevant questions for review.
        </Alert>

        <Form onSubmit={handleSubmit}>
          {/* Exam Type Selection */}
          <Row>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label">
                  Exam Type <span className="text-danger">*</span>
                </Label>
                <Select
                  options={EXAM_TYPE_OPTIONS}
                  value={{
                    value: formData.examType,
                    label: EXAM_TYPE_LABELS[formData.examType]
                  }}
                  onChange={(option) => setFormData({ ...formData, examType: option?.value || 'mcat' })}
                  styles={customDropdownWithBorder}
                />
                <small className="text-muted">
                  {formData.examType === 'dat'
                    ? 'DAT-style: concise science questions, natural science focus, dental-relevant topics'
                    : formData.examType === 'pmr'
                    ? 'PM&R Board-style: clinical vignettes, rehabilitation medicine, diagnostic reasoning'
                    : 'MCAT-style: multi-step reasoning, research passages, cross-discipline integration'}
                </small>
              </FormGroup>
            </Col>
          </Row>

          {/* Subject, Unit, and Topic Selection */}
          <Row>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label">
                  Subject <span className="text-danger">*</span>
                </Label>
                <Select
                  options={subjects.map(subject => ({
                    value: subject.subjectId,
                    label: subject.name
                  }))}
                  value={
                    formData.subjectId
                      ? { value: formData.subjectId, label: subjects.find(s => s.subjectId === formData.subjectId)?.name }
                      : null
                  }
                  onChange={(option) => setFormData({ ...formData, subjectId: option?.value || '', unitId: '', topicId: '' })}
                  styles={customDropdownWithBorder}
                  placeholder="Select a subject"
                  isClearable
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label">
                  Unit <span className="text-danger">*</span>
                </Label>
                <Select
                  options={filteredUnits.map(unit => ({
                    value: unit.unitId,
                    label: unit.name
                  }))}
                  value={
                    formData.unitId
                      ? { value: formData.unitId, label: filteredUnits.find(u => u.unitId === formData.unitId)?.name }
                      : null
                  }
                  onChange={(option) => setFormData({ ...formData, unitId: option?.value || '', topicId: '' })}
                  styles={customDropdownWithBorder}
                  placeholder="Select a unit"
                  isDisabled={!formData.subjectId}
                  isClearable
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label">
                  Topic <small className="text-muted font-weight-normal">(Optional)</small>
                </Label>
                <Select
                  options={filteredTopics.map(topic => ({
                    value: topic.topicId,
                    label: topic.name
                  }))}
                  value={
                    formData.topicId
                      ? { value: formData.topicId, label: filteredTopics.find(t => t.topicId === formData.topicId)?.name }
                      : null
                  }
                  onChange={(option) => setFormData({ ...formData, topicId: option?.value || '' })}
                  styles={customDropdownWithBorder}
                  placeholder="Select a topic (optional)"
                  isDisabled={!formData.unitId || filteredTopics.length === 0}
                  isClearable
                />
                <small className="text-muted">
                  Narrow the focus to a specific topic, or leave blank to cover the entire unit
                </small>
              </FormGroup>
            </Col>
          </Row>

          {/* Passage-Based Question Toggle */}
          <FormGroup>
            <div className="custom-control custom-checkbox">
              <input
                className="custom-control-input"
                id="aiGeneratorPassageCheckbox"
                type="checkbox"
                checked={formData.isPassageBased}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    isPassageBased: e.target.checked,
                    passageMode: 'new',
                    existingPassageId: ''
                  });
                }}
              />
              <label
                className="custom-control-label"
                htmlFor="aiGeneratorPassageCheckbox"
                style={{ color: '#32325d', fontWeight: '600' }}
              >
                {getPassageCheckboxLabel()}
              </label>
            </div>
            <small className="text-muted ml-4">
              {getPassageDescription()}
            </small>
          </FormGroup>

          {/* Passage Mode Selection */}
          {formData.isPassageBased && (
            <FormGroup>
              <Label className="form-control-label" style={{ color: '#32325d', fontWeight: '600' }}>Passage Option</Label>
              <div>
                <div className="custom-control custom-radio mb-2">
                  <input
                    className="custom-control-input"
                    id="passageModeNew"
                    type="radio"
                    name="passageMode"
                    checked={formData.passageMode === 'new'}
                    onChange={() => setFormData({ ...formData, passageMode: 'new', existingPassageId: '' })}
                  />
                  <label className="custom-control-label" htmlFor="passageModeNew">
                    {formData.examType === 'pmr' ? 'Generate new clinical vignette' : 'Generate new passage'}
                  </label>
                </div>
                <div className="custom-control custom-radio">
                  <input
                    className="custom-control-input"
                    id="passageModeExisting"
                    type="radio"
                    name="passageMode"
                    checked={formData.passageMode === 'existing'}
                    onChange={() => setFormData({ ...formData, passageMode: 'existing' })}
                  />
                  <label className="custom-control-label" htmlFor="passageModeExisting">
                    Use existing passage
                  </label>
                </div>
              </div>
              <small className="text-muted d-block mt-2">
                {formData.passageMode === 'new'
                  ? formData.examType === 'pmr'
                    ? 'AI will create a new clinical vignette and generate questions for it'
                    : 'AI will create a new passage and generate questions for it'
                  : 'AI will generate questions for a passage you already created'}
              </small>
            </FormGroup>
          )}

          {/* Figure/Diagram Generation Option */}
          {formData.isPassageBased && formData.passageMode === 'new' && (
            <FormGroup>
              <div className="custom-control custom-checkbox">
                <input
                  className="custom-control-input"
                  id="generateFigureCheckbox"
                  type="checkbox"
                  checked={formData.generateFigure}
                  onChange={(e) => setFormData({ ...formData, generateFigure: e.target.checked })}
                  disabled={!imageGenAvailable}
                />
                <label
                  className="custom-control-label"
                  htmlFor="generateFigureCheckbox"
                  style={{ color: imageGenAvailable ? '#32325d' : '#8898aa', fontWeight: '600' }}
                >
                  <i className="fas fa-chart-bar mr-2"></i>
                  Include AI-generated figure/diagram in passage
                </label>
              </div>
              {!imageGenAvailable ? (
                <Alert color="warning" className="mt-2 mb-0 py-2">
                  <small>
                    <i className="fas fa-exclamation-triangle mr-1"></i>
                    <strong>Image generation not configured.</strong> Add <code>REACT_APP_GEMINI_API_KEY</code> to your .env file.
                  </small>
                </Alert>
              ) : (
                <small className="text-muted ml-4">
                  AI will generate a scientific diagram or chart using Gemini (free tier available)
                </small>
              )}
            </FormGroup>
          )}

          {/* Existing Passage Selection */}
          {formData.isPassageBased && formData.passageMode === 'existing' && (
            <FormGroup>
              <Label className="form-control-label" style={{ color: '#32325d', fontWeight: '600' }}>
                Select Existing Passage <span className="text-danger">*</span>
              </Label>
              <Select
                options={filteredPassages.map(passage => ({
                  value: passage.passageId,
                  label: passage.title
                }))}
                value={
                  formData.existingPassageId
                    ? {
                        value: formData.existingPassageId,
                        label: filteredPassages.find(p => p.passageId === formData.existingPassageId)?.title
                      }
                    : null
                }
                onChange={(option) => setFormData({ ...formData, existingPassageId: option?.value || '' })}
                styles={customDropdownWithBorder}
                placeholder={
                  filteredPassages.length === 0
                    ? "No passages available for this subject/unit"
                    : "Select a passage"
                }
                isDisabled={!formData.subjectId || !formData.unitId || filteredPassages.length === 0}
                isClearable
              />
              {formData.subjectId && formData.unitId && filteredPassages.length === 0 && (
                <small className="text-warning d-block mt-1">
                  <i className="fas fa-exclamation-triangle mr-1"></i>
                  No passages found for this subject/unit. Please create a passage first in the "Manage Passages" tab.
                </small>
              )}
              {filteredPassages.length > 0 && (
                <small className="text-muted">
                  {`${filteredPassages.length} passage${filteredPassages.length !== 1 ? 's' : ''} available`}
                </small>
              )}
            </FormGroup>
          )}

          {/* Additional Context */}
          <FormGroup>
            <Label className="form-control-label">Additional Context (Optional)</Label>
            <Input
              type="textarea"
              rows="4"
              placeholder="Provide any additional context, specific details, or requirements for the questions..."
              value={formData.additionalContext}
              onChange={(e) => setFormData({ ...formData, additionalContext: e.target.value })}
            />
            <small className="text-muted">
              Add any specific requirements, learning objectives, or context that should be considered
            </small>
          </FormGroup>

          {/* AI Model, Number of Questions, and Choices */}
          <Row>
            <Col md="4">
              <FormGroup>
                <Label className="form-control-label">
                  AI Model <span className="text-danger">*</span>
                </Label>
                <Select
                  options={[
                    { value: 'gemini', label: 'Gemini 2.0 Flash', icon: 'google' },
                    { value: 'chatgpt', label: 'ChatGPT (GPT-4o-mini)', icon: 'robot' }
                  ]}
                  value={
                    formData.aiModel === 'gemini'
                      ? { value: 'gemini', label: 'Gemini 2.0 Flash' }
                      : { value: 'chatgpt', label: 'ChatGPT (GPT-4o-mini)' }
                  }
                  onChange={(option) => setFormData({ ...formData, aiModel: option.value })}
                  styles={customDropdownWithBorder}
                />
                <small className="text-muted">
                  {formData.aiModel === 'gemini'
                    ? 'Google Gemini — Free tier'
                    : 'OpenAI ChatGPT — Pay per use'}
                </small>
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="form-control-label">
                  Number of Questions <span className="text-danger">*</span>
                </Label>
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={formData.count}
                  onChange={(e) => setFormData({ ...formData, count: parseInt(e.target.value) || 1 })}
                  required
                />
                <small className="text-muted">
                  Generate between 1-20 questions at a time (recommended: 5-10)
                </small>
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="form-control-label">
                  Number of Choices <span className="text-danger">*</span>
                </Label>
                <Select
                  options={[
                    { value: 2, label: '2 choices' },
                    { value: 3, label: '3 choices' },
                    { value: 4, label: '4 choices (recommended)' },
                    { value: 5, label: '5 choices' }
                  ]}
                  value={{
                    value: formData.numberOfChoices,
                    label: formData.numberOfChoices === 4
                      ? '4 choices (recommended)'
                      : `${formData.numberOfChoices} choices`
                  }}
                  onChange={(option) => setFormData({ ...formData, numberOfChoices: option.value })}
                  styles={customDropdownWithBorder}
                />
                <small className="text-muted">
                  Number of answer choices per question
                </small>
              </FormGroup>
            </Col>
          </Row>

          {/* Submit Button */}
          <div className="text-right">
            <Button
              color="primary"
              type="submit"
              disabled={loading || generationInProgress}
              size="lg"
            >
              {generationInProgress ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                  Generating Questions...
                </>
              ) : (
                <>
                  <i className="fas fa-magic mr-2"></i>
                  Generate Questions with AI
                </>
              )}
            </Button>
          </div>
        </Form>
      </CardBody>
    </Card>
  );
};

export default AIQuestionGenerator;
